'use client';

import { useSessionStore } from '@/app/store/use-session-store';
import {
  AddressPoint,
  PointStatus,
  useAddressStore,
} from '@/entities/address/model/use-address-store';
import { useRouter } from '@/i18n/navigation';
import { useNavigationState } from '@/shared/store/use-navigation-state';
import { toaster } from '@/shared/ui/toaster';
import { useTranslations } from 'next-intl';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Mobile from './responsive/mobile';

export interface IProps {
  city: string;
  coords: [number, number];
  address: string;
  accommodation: string;
  onClear: () => void;
  isLoading?: boolean;
}

const initial: AddressPoint = {
  name: '',
  address: '',
  floor: '',
  apartment: '',
  accommodation: '',
  to_address_description: '',
  location: [0, 0],
  status: PointStatus.INACTIVE,
  id: '',
  city: '',
  building: '',
};

const AddressDetails = ({
  city,
  coords,
  address,
  accommodation,
  onClear,
}: IProps) => {
  const t = useTranslations();
  const editingPoint = useAddressStore((state) => state.editing_point);
  const updatePoint = useAddressStore((state) => state.updatePoint);
  const acceptGeoL = useSessionStore((state) => state.acceptGeoL);
  const addPoint = useAddressStore((state) => state.addPoint);
  const closeMap = useAddressStore((state) => state.closeMap);

  const [formData, setFormData] = useState(initial);
  const { redirectTo, clearRedirectTo } = useNavigationState();
  const router = useRouter();

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Clear the address input field
  const clearInput = () => {
    setFormData((prev) => ({ ...prev, name: '' }));
  };

  const saveAddress = () => {
    if (coords?.length !== 2) {
      return toaster.create({
        title: t('location.has_not_been_detected'),
        description: t('please_enter_address'),
        type: 'error',
      });
    }

    const data: AddressPoint = {
      ...formData,
      city,
      location: coords,
      id: formData?.id ? formData?.id : uuidv4(),
    };

    if (editingPoint) {
      updatePoint(data);
      toaster.create({
        title: t('address_successfully_updated'),
        type: 'success',
      });
    } else {
      acceptGeoL();
      addPoint(data);
      toaster.create({
        title: t('address_successfully_added'),
        type: 'success',
      });
    }

    if (redirectTo) {
      router.push(redirectTo);
      clearRedirectTo();
    }

    setFormData(initial);
    closeMap();

    // there was router.refresh after 500ms
    onClear();
  };

  useEffect(() => {
    setFormData((prev) => ({ ...prev, address, accommodation }));
  }, [address, accommodation]);

  useEffect(() => {
    if (editingPoint) {
      setFormData(editingPoint);
    }
  }, [editingPoint]);

  return (
    <Mobile
      city={city}
      address={address}
      formData={formData}
      clearInput={clearInput}
      saveAddress={saveAddress}
      handleInputChange={handleInputChange}
    />
  );
};

export default AddressDetails;
